import { useCallback, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import type { UseFormReturn } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useChannel } from '@/lib/realtime/useChannel';
import { boatKeys } from '@/modules/inventory/hooks/useBoats';
import { boatApi } from '@/modules/inventory/services/boatApi';
import type { Boat, NotImportedOptionLine } from '@/modules/inventory/types/boat.types';
import type { WizardFormData } from '@/modules/inventory/components/boat-form/types/wizard.types';
import {
  buildUpdatePayload,
  diffBoat,
  rebase,
  sameValue,
  stableStringify,
  type EditableBoat,
  type ScalarValue,
} from '@/modules/inventory/utils/boat-changes';
import { describeLostRow } from '@/modules/inventory/utils/boat-change-labels';
import { clearPendingChanges, readPendingChanges } from '@/modules/inventory/utils/pending-boat-changes';
import {
  CANVAS_DEFERRED_KEYS,
  CANVAS_DISPLAY_KEYS,
  CANVAS_SCALAR_KEYS,
  blankCanvasEditable,
  boatToCanvasEditable,
  boatToCanvasValues,
  canvasToEditable,
  dropSavedNewRows,
  editableToCanvasValues,
  pairAddedRows,
  withDeferredKeysFrom,
} from '../lib/canvasEditable';

/**
 * #1178 / #1184 (spec §2) — the ONE way server data reaches the canvas form.
 *
 * Replaces the mount-time resume reset, the blank-new backstop, the 2 s arming
 * timers and the streamed-field writes. The form shows only the server's merged
 * boat, re-read fresh (never from cache):
 *   - on mount and whenever the boat id is set (a resumed or a brand-new canvas),
 *   - on `phase_done` and on the run terminal,
 *   - after a realtime reconnect,
 *   - every 15 s while a run is in progress.
 * The first read always applies; later reads apply only when `version` is newer.
 * Fields the dealer changed since the last snapshot keep the dealer's value
 * (rebase, mine wins) and the saved value is offered on the field.
 *
 * The snapshot is updated BEFORE any form write, so a re-read never leaves a
 * difference for autosave to send.
 */

export const CANVAS_POLL_MS = 15_000;
/** 4F3 review I3 — a failed first read is retried after this long until the canvas hydrates. */
export const CANVAS_FIRST_READ_RETRY_MS = 3_000;

export interface CanvasRunState {
  status: string;
  phase: string;
  epoch: number;
  isActive: boolean;
  failedCount: number;
}

export type CanvasRunEvent =
  | { type: 'phase_done'; phase: string; epoch: number; failedCount: number; source: 'live' }
  | { type: 'done' | 'done_with_errors' | 'failed'; epoch: number; userMessage?: string; source: 'live' | 'poll' };

type TerminalType = 'done' | 'done_with_errors' | 'failed';
const TERMINALS: ReadonlySet<string> = new Set(['done', 'done_with_errors', 'failed']);

export interface UseCanvasHydrationArgs {
  methods: UseFormReturn<WizardFormData>;
  boatId: string | null;
  tenantId: string | null;
  /** True from "Confirm & extract" until the run ends: poll before the server reports the run. */
  runStarted: boolean;
  onRunEvent?: (event: CanvasRunEvent) => void;
}

export interface CanvasHydration {
  hasHydrated: boolean;
  run: CanvasRunState | null;
  isRunActive: boolean;
  /** Field → the saved value, for fields the dealer changed that also changed on the server. */
  serverValues: Record<string, ScalarValue>;
  dismissServerValue: (field: string) => void;
  /** The last server snapshot the form is based on. */
  getSnapshot: () => EditableBoat | null;
  /** The form now, in the changes model (null until the first read). */
  readForm: () => EditableBoat | null;
  refresh: () => Promise<void>;
  /**
   * A save landed: the saved boat is the snapshot; fields not edited since `started` take the saved value.
   * `started` is the editable the save began from: the `current` passed to the save, not `outcome.sent`
   * (after a silent 409 rebase `sent` carries the server's values, S4-35).
   */
  adopt: (boat: Boat, started: EditableBoat) => void;
  /** The dealer chose per field after a 409: `desired` goes into the form over the fresh boat. */
  reconcile: (fresh: Boat, desired: EditableBoat) => void;
  /** #1182 — the option lines the latest read reported as not imported. */
  notImportedOptions: NotImportedOptionLine[];
}

function runStateOf(boat: Boat): CanvasRunState | null {
  const run = boat.extraction_run;
  if (!run) return null;
  return {
    status: run.status,
    phase: run.phase,
    epoch: run.epoch,
    isActive: run.is_active,
    failedCount: run.failed_count,
  };
}

/** ai-engine publishes raw at ctx.data; Laravel broadcasts may wrap in {payload}. */
function unwrap(env: unknown): Record<string, unknown> {
  const raw = env as Record<string, unknown> | null;
  if (raw && typeof raw === 'object' && 'type' in raw) return raw;
  return ((raw as { payload?: Record<string, unknown> } | null)?.payload ?? {}) as Record<string, unknown>;
}

export function useCanvasHydration({
  methods,
  boatId,
  tenantId,
  runStarted,
  onRunEvent,
}: UseCanvasHydrationArgs): CanvasHydration {
  const queryClient = useQueryClient();
  const snapshotRef = useRef<EditableBoat | null>(null);
  const trailerIdRef = useRef<string | null>(null);
  const runRef = useRef<CanvasRunState | null>(null);
  /** Epochs whose terminal was reported (live or by a read): each is reported once. */
  const reportedTerminalEpochsRef = useRef<Set<number>>(new Set());
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runStartedRef = useRef(runStarted);
  runStartedRef.current = runStarted;
  const hasSubscribedRef = useRef(false);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const againRef = useRef(false);
  const boatIdRef = useRef(boatId);
  boatIdRef.current = boatId;
  const onRunEventRef = useRef(onRunEvent);
  onRunEventRef.current = onRunEvent;

  const [hasHydrated, setHasHydrated] = useState(false);
  const [run, setRun] = useState<CanvasRunState | null>(null);
  const [serverValues, setServerValues] = useState<Record<string, ScalarValue>>({});
  const [notImportedOptions, setNotImportedOptions] = useState<NotImportedOptionLine[]>([]);

  // A different boat (or none): start over.
  useEffect(() => {
    snapshotRef.current = null;
    trailerIdRef.current = null;
    runRef.current = null;
    reportedTerminalEpochsRef.current = new Set();
    hasSubscribedRef.current = false;
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
    setHasHydrated(false);
    setRun(null);
    setServerValues({});
    setNotImportedOptions([]);
  }, [boatId]);

  const readForm = useCallback((): EditableBoat | null => {
    const snapshot = snapshotRef.current;
    if (!snapshot) return null;
    return canvasToEditable(methods.getValues(), snapshot.version, trailerIdRef.current);
  }, [methods]);

  const getSnapshot = useCallback(() => snapshotRef.current, []);

  /** Write an editable state into the form, touching only what differs. */
  const writeForm = useCallback(
    (editable: EditableBoat, boat: Boat) => {
      const next = editableToCanvasValues(editable, boat) as Record<string, unknown>;
      const current = methods.getValues() as unknown as Record<string, unknown>;
      for (const [key, value] of Object.entries(next)) {
        if (stableStringify(current[key]) === stableStringify(value)) continue;
        methods.setValue(key as never, value as never, { shouldDirty: false });
      }
    },
    [methods],
  );

  const takeSnapshot = useCallback((boat: Boat): EditableBoat => {
    const theirs = boatToCanvasEditable(boat);
    snapshotRef.current = theirs;
    trailerIdRef.current = boat.trailers?.[0]?.id ?? null;
    return theirs;
  }, []);

  const trackRun = useCallback((boat: Boat) => {
    const previous = runRef.current;
    const next = runStateOf(boat);
    runRef.current = next;
    setRun(next);
    setNotImportedOptions(boat.not_imported_options ?? []);
    // A missed terminal (dropped event): a read saw the run end. Either a read saw
    // it running, or this canvas started it and it ended between two reads (4F3
    // review I2: otherwise the canvas stays "reading…" and polls forever).
    if (!next || next.isActive || !TERMINALS.has(next.status) || reportedTerminalEpochsRef.current.has(next.epoch)) return;
    const sawItRun = Boolean(previous?.isActive && next.epoch === previous.epoch);
    const startedHere = runStartedRef.current && (!previous || previous.isActive || next.epoch > previous.epoch);
    if (sawItRun || startedHere) {
      reportedTerminalEpochsRef.current.add(next.epoch);
      onRunEventRef.current?.({ type: next.status as TerminalType, epoch: next.epoch, source: 'poll' });
    }
  }, []);

  const apply = useCallback(
    (boat: Boat) => {
      if (boat.id !== boatIdRef.current) return; // a response for a boat this canvas left
      trackRun(boat);

      const previous = snapshotRef.current;
      const theirs = boatToCanvasEditable(boat);
      if (previous && theirs.version <= previous.version) return; // not newer: the form stays

      const pending = previous ? null : readPendingChanges('canvas', boat.id);
      const base = previous ?? pending?.base ?? blankCanvasEditable();
      const mine = pending?.current
        ?? canvasToEditable(methods.getValues(), base.version, trailerIdRef.current);
      const { merged, conflicts, lostRows } = rebase(base, mine, theirs);
      const next = pairAddedRows(merged, base);

      takeSnapshot(boat); // BEFORE the writes: autosave diffs against it
      writeForm(next, boat);

      if (!previous) {
        const display = boatToCanvasValues(boat) as unknown as Record<string, unknown>;
        for (const key of CANVAS_DISPLAY_KEYS) {
          if (methods.getValues(key) === '' && display[key]) {
            methods.setValue(key, display[key] as never, { shouldDirty: false });
          }
        }
      }

      if (pending) {
        const { isEmpty } = buildUpdatePayload(diffBoat(theirs, withDeferredKeysFrom(next, theirs)), theirs.version);
        if (isEmpty) clearPendingChanges('canvas', boat.id);
        else toast.info('We restored changes you had not saved.');
      }

      for (const row of lostRows) {
        toast.error(`${describeLostRow(row)} was removed elsewhere; your changes to it were not kept.`);
      }

      setServerValues((prev) => {
        const out: Record<string, ScalarValue> = { ...prev };
        for (const conflict of conflicts) {
          if ((CANVAS_SCALAR_KEYS as readonly string[]).includes(conflict.path)) out[conflict.path] = conflict.theirs as ScalarValue;
        }
        for (const key of Object.keys(out)) {
          if (sameValue(next.scalars[key], theirs.scalars[key])) delete out[key]; // the dealer's value is the saved one now
          else out[key] = theirs.scalars[key] ?? null;
        }
        return out;
      });

      // A newer version means the merge (or a save elsewhere) moved provenance too.
      if (previous) void queryClient.invalidateQueries({ queryKey: ['field-sources', boat.id] });
      setHasHydrated(true);
    },
    [methods, queryClient, takeSnapshot, trackRun, writeForm],
  );

  const refresh = useCallback((): Promise<void> => {
    if (!boatIdRef.current) return Promise.resolve();
    if (inFlightRef.current) {
      againRef.current = true;
      return inFlightRef.current;
    }
    const task = (async () => {
      do {
        againRef.current = false;
        const id = boatIdRef.current;
        if (!id) break;
        try {
          const boat = await queryClient.fetchQuery({
            queryKey: boatKeys.detail(id),
            queryFn: () => boatApi.getBoat(id),
            staleTime: 0,
          });
          apply(boat);
        } catch (error) {
          console.warn('[useCanvasHydration] re-read failed', error);
          // 4F3 review I3 — until the first read lands the canvas has no snapshot and
          // autosave is not armed: retry, or what the dealer types is never saved.
          // Only a failure that can heal (no response, or a 5xx): a 403/404 will not.
          const status = isAxiosError(error) ? error.response?.status : undefined;
          const canHeal = status === undefined || status >= 500;
          if (canHeal && !snapshotRef.current && boatIdRef.current === id && !retryTimerRef.current) {
            retryTimerRef.current = setTimeout(() => {
              retryTimerRef.current = null;
              if (boatIdRef.current === id && !snapshotRef.current) void refreshRef.current();
            }, CANVAS_FIRST_READ_RETRY_MS);
          }
        }
      } while (againRef.current);
      inFlightRef.current = null;
    })();
    inFlightRef.current = task;
    return task;
  }, [apply, queryClient]);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => () => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
  }, []);

  // Mount, and whenever the canvas gets a boat (resume or a freshly created draft).
  useEffect(() => {
    if (boatId) void refresh();
  }, [boatId, refresh]);

  // Every 15 s while a run is in progress (or was just started from this canvas).
  const isRunActive = Boolean(run?.isActive);
  useEffect(() => {
    if (!boatId || !(isRunActive || runStarted)) return;
    const timer = setInterval(() => {
      void refresh();
    }, CANVAS_POLL_MS);
    return () => clearInterval(timer);
  }, [boatId, isRunActive, runStarted, refresh]);

  const channel = tenantId && boatId ? `tenant.${tenantId}.copilot.${boatId}` : null;
  useChannel<Record<string, unknown>>(channel, {
    onSubscribed: () => {
      if (!hasSubscribedRef.current) {
        hasSubscribedRef.current = true; // the mount effect owns the first read
        return;
      }
      void refresh(); // a reconnect may have missed phase_done or the terminal
    },
    onPublication: (env) => {
      const event = unwrap(env);
      const type = typeof event.type === 'string' ? event.type : '';
      const epoch = typeof event.epoch === 'number' ? event.epoch : 0;

      if (type === 'phase_done') {
        onRunEventRef.current?.({
          type: 'phase_done',
          phase: typeof event.phase === 'string' ? event.phase : '',
          epoch,
          failedCount: typeof event.failed_count === 'number' ? event.failed_count : 0,
          source: 'live',
        });
        void refresh();
        return;
      }

      if (type === 'suggestions_ready') {
        // #1307 — the spec lookup (or a Propose run) recorded suggestions: the form
        // values did not change, the provenance did. Re-read both.
        if (boatIdRef.current) void queryClient.invalidateQueries({ queryKey: ['field-sources', boatIdRef.current] });
        void refresh();
        return;
      }

      if (TERMINALS.has(type)) {
        const reported = reportedTerminalEpochsRef.current.has(epoch);
        reportedTerminalEpochsRef.current.add(epoch);
        if (!reported) onRunEventRef.current?.({
          type: type as TerminalType,
          epoch,
          ...(typeof event.user_message === 'string' ? { userMessage: event.user_message } : {}),
          source: 'live',
        });
        void refresh();
      }
      // Field, insight, status and logged events are the progress feed's (useCopilotSession).
    },
  });

  const adopt = useCallback(
    (boat: Boat, started: EditableBoat) => {
      if (boat.id !== boatIdRef.current) return;
      trackRun(boat);
      const mineNow = canvasToEditable(methods.getValues(), started.version, trailerIdRef.current);
      const theirs = takeSnapshot(boat);
      const { sent: startedBase, mine } = dropSavedNewRows(started, mineNow, theirs);
      const { merged } = rebase(startedBase, mine, theirs);
      writeForm(pairAddedRows(merged, startedBase), boat);
    },
    [methods, takeSnapshot, trackRun, writeForm],
  );

  const reconcile = useCallback(
    (fresh: Boat, desired: EditableBoat) => {
      if (fresh.id !== boatIdRef.current) return;
      const form = canvasToEditable(methods.getValues(), desired.version, trailerIdRef.current);
      const scalars = { ...desired.scalars };
      for (const key of CANVAS_DEFERRED_KEYS) scalars[key] = form.scalars[key] ?? null; // the Status stays the dealer's
      takeSnapshot(fresh);
      writeForm({ ...desired, scalars }, fresh);
    },
    [methods, takeSnapshot, writeForm],
  );

  const dismissServerValue = useCallback((field: string) => {
    setServerValues((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  return {
    hasHydrated,
    run,
    isRunActive,
    serverValues,
    dismissServerValue,
    getSnapshot,
    readForm,
    refresh,
    adopt,
    reconcile,
    notImportedOptions,
  };
}
